import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { List, ListItem, ListItemText, LinearProgress, Grid, Box, useTheme } from '@mui/material';
import {
	userAccountsSelector,
	tokenSelector,
	currentAccountIdSelector,
	userDetailsLoadingSelector
} from 'reducers/userAuthSlice';
import { $backgroundOffWhite, $black, $white, authenticationPagesStyles } from 'constants/styles';
import { CURRENT_ACCOUNT_ID } from 'constants/localStorageConstants';
import AirControl from 'assets/images/air_control_black.png';
import AloftLogo from 'assets/images/aloft_logo_black.png';
import { resetAppStateOnSwitchAccount } from 'actions/general';
import { useTranslation } from 'react-i18next';
import LanguageSwitcherWrap from 'components/elements/LanguageSwitcherWrap';

const AccountPicker = () => {
	const theme = useTheme();
	const styles = {
		...authenticationPagesStyles(theme),
		main: {
			height: '100%',
			width: '100%',
			overflow: 'auto',
			padding: '40px',
			backgroundColor: $backgroundOffWhite,
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'stretch',
			justifyContent: 'space-between',
			[theme.breakpoints.down('md')]: {
				alignItems: 'center',
				padding: '30px'
			}
		},
		logo: {
			marginBottom: '30px',
			width: '215px'
		},
		airControlLogo: {
			width: '433px',
			marginBottom: '20px',
			[theme.breakpoints.down('md')]: {
				width: '100%'
			}
		},
		title: {
			fontSize: '30px',
			marginBottom: '30px'
		},
		list: {
			color: $black,
			backgroundColor: $white,
			maxHeight: 'calc(100% - 73px)',
			overflow: 'auto',
			minWidth: '280px'
		},
		airControlContainer: {
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
			marginBottom: '30px',
			fontWeight: '800px',
			[theme.breakpoints.down('md')]: {
				marginBottom: '30px'
			}
		},
		welcome: {
			color: $black,
			fontSize: '50px',
			marginBottom: '15px',
			[theme.breakpoints.down('md')]: {
				display: 'none'
			}
		},
		footerPlaceholder: {
			height: '78px'
		}
	};

	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const accounts = useSelector(userAccountsSelector);
	const token = useSelector(tokenSelector);
	const currentAccountId = useSelector(currentAccountIdSelector);
	const [accountExpired, setAccountExpired] = useState(false);
	const userDetailsLoading = useSelector(userDetailsLoadingSelector);

	const handleClick = account => {
		if (
			account?.subscription_state === 'expired' ||
			account?.subscription_state === 'trial_expired'
		) {
			setAccountExpired(true);
			navigate('/no-accounts');
		} else {
			const { id } = account;
			localStorage.setItem(CURRENT_ACCOUNT_ID, id);
			dispatch(resetAppStateOnSwitchAccount(id));
			navigate(`/account/${id}/airspace`);
		}
	};

	useEffect(() => {
		if (
			accounts &&
			(accounts?.length === 0 ||
				(accounts.length === 1 &&
					(accounts[0]?.subscription_state === 'expired' ||
						accounts[0]?.subscription_state === 'trial_expired')))
		) {
			setAccountExpired(true);
			return navigate('/no-accounts');
		}
	}, [accounts, navigate]);

	useEffect(() => {
		// If user landed into page without going through post login flow, redirect them to the flow
		if (!accounts) {
			return navigate('/post-login-flow');
		}
		// check if user is Authenticated and active Selected Account
		if (Boolean(token) && Boolean(currentAccountId) && accounts?.length <= 1 && !accountExpired) {
			return navigate(`/account/${currentAccountId}/airspace`);
		}
	}, [accountExpired, accounts, currentAccountId, navigate, token]);

	return accounts && !accountExpired ? (
		<LanguageSwitcherWrap>
			<Box sx={styles.main}>
				<Box sx={styles.headerContainer}>
					<Box component='img' src={AloftLogo} alt='' sx={styles.logo} />
				</Box>
				<Grid container sx={styles.mainContainer}>
					<Grid item xs={12} sm={12} md={6} sx={styles.leftContainerText}>
						<Box sx={styles.airControlContainer}>
							<Box sx={styles.welcome}>{t('Welcome to')}</Box>
							<Box component={'img'} src={AirControl} alt='' sx={styles.airControlLogo} />
						</Box>
					</Grid>
					<Grid item xs={12} sm={12} md={6}>
						<Box sx={styles.outlineContainer}>
							<Box style={{ height: '4px' }}>{userDetailsLoading && <LinearProgress />}</Box>
							<Box sx={styles.title}>{t('Select Account')}</Box>
							<List open sx={styles.list}>
								{accounts?.map(account => (
									<ListItem button key={account.id} onClick={() => handleClick(account)}>
										<ListItemText primary={account.account_name} />
									</ListItem>
								))}
							</List>
						</Box>
					</Grid>
				</Grid>
				<Box sx={styles.footerPlaceholder} />
			</Box>
		</LanguageSwitcherWrap>
	) : null;
};

export default AccountPicker;
